import { Button } from "@astryxdesign/core";
import { useCallback, useEffect, useRef, useState } from "react";
import { openPilot } from "@/lib/pilot";
import { INTEGRATIONS } from "@/integrations";
import { Iphone, Win } from "./Device";
import { Scribble } from "./ink";
import { LoopNotes } from "./LoopNotes";
import { FILM, FilmUI, PHONE, TALL_FILM } from "./Reel";

/** The rental systems, as their own marks in a row, as VXT lists its integrations. */
const SYSTEMS = [
  { src: "/logos/current-rms-mark.png", name: "Current RMS" },
  { src: "/logos/rentman-mark.svg", name: "Rentman" },
  { src: "/logos/booqable-mark.svg", name: "Booqable" },
  { src: "/logos/hirehop-mark.svg", name: "HireHop" },
];

/**
 * The top of the page, on the lime: the headline, the two asks, the rental
 * systems it works with, and the app under them, the Phone page on a desk with the same conversation open on a
 * phone over its corner. The headline is the app, every enquiry in one place,
 * not the receptionist, which is the extra. The screenshots are the admin app with the film's
 * story in it (Hannah ringing about James's farewell party), so the page, the
 * film and the app all tell the one story.
 */
export function Intro() {
  const film = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  // A phone, or any screen taller than it is wide, gets the film cut tall,
  // with its notes drawn over it, as the reel does: the wide film filling a
  // tall screen would lose her card off its side.
  const [tall] = useState(() => matchMedia(`${PHONE}, (orientation: portrait)`).matches);

  const stop = useCallback(() => {
    film.current?.pause();
    setPlaying(false);
  }, []);

  // See how it works: the film over the whole screen. Started in the press
  // itself, as a phone plays sound only from inside one, so the player is
  // always in the page and only shown while it plays.
  const play = useCallback(() => {
    const v = film.current;
    if (!v) return;
    v.currentTime = 0;
    setPlaying(true);
    void v.play();
  }, []);

  // Esc stops it, and so does scrolling the page on under it, as the reel's
  // film does. There is no close button: over the whole screen it would sit
  // where the film's notes want to be.
  useEffect(() => {
    if (!playing) return;
    const from = scrollY;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && stop();
    const onScroll = () => Math.abs(scrollY - from) > 80 && stop();
    addEventListener("keydown", onKey);
    addEventListener("scroll", onScroll, { passive: true });
    return () => {
      removeEventListener("keydown", onKey);
      removeEventListener("scroll", onScroll);
    };
  }, [playing, stop]);

  return (
    <section className="zone lime intro">
      <div className="col words">
        <h1>
          Every hire enquiry, in <Scribble>one place</Scribble>.
        </h1>
        <p className="lede">
          HireOS is the shared phone and inbox for your hire team, with every
          call, text and customer in one place, so no hire slips through.
        </p>
        <div className="btns c">
          <Button label="Try for free" variant="ghost" onClick={openPilot} />
          <Button
            label="See how it works"
            variant="ghost"
            className="bare"
            icon={
              <svg className="tri" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M7 6.9V17.1Q7 19.5 9.058 18.265L17.442 13.235Q19.5 12 17.442 10.765L9.058 5.735Q7 4.5 7 6.9Z" />
              </svg>
            }
            onClick={play}
          />
        </div>
        <div className="rms">
          {SYSTEMS.map((s) => (
            <span className="chip" key={s.name} title={s.name}>
              <img src={s.src} alt={s.name} />
            </span>
          ))}
          <a className="pill" href="/integrations">
            +{INTEGRATIONS.length - SYSTEMS.length} more integrations
          </a>
        </div>
        <p className="avail">Available on iOS, Android and the web.</p>
      </div>

      <div className="col shots">
        <Win
          eager
          src="/shots/d-phone-call.webp"
          alt="HireOS on a desk: the Phone page, on a call with Hannah Reid, her calls and texts open beside it"
        />
        <Iphone
          eager
          src="/shots/m-thread.webp"
          alt="HireOS on a phone: the same conversation with Hannah Reid"
        />
      </div>

      <div className={`hplay${tall ? " tall" : ""}`} hidden={!playing}>
        <video
          ref={film}
          src={tall ? TALL_FILM : FILM}
          playsInline
          preload="none"
          aria-label="How HireOS works: one call, start to finish"
          onEnded={stop}
        />
        {tall && playing && <LoopNotes loop={film} tall film />}
        <FilmUI film={film} playing={playing} />
      </div>
    </section>
  );
}
